import { commonHeader, kintoneSystemEndpoints } from "~/config/apiConfig";
import type { DevelopmentStep } from "~/interface/kintoneSystem";

// ジョブのステータス
export type KintoneSystemStatus = "queued" | "running" | "succeeded" | "failed" | "canceled";

// ジョブ作成リクエスト
export interface StartKintoneSystemRequest {
  subdomain: string;
  space_name: string;
  login_id: string;
  password: string;
  include_sample_data: boolean;
  /**
   * アプリごとに適用するカスタマイズ機能名リスト。
   * 省略時はカスタマイズ仕様書の全機能を適用。
   * キーはアプリ名、値は機能名の配列。
   */
  selected_customize_ids?: Record<string, string[]>;
  /**
   * true の場合、アプリ生成回数の代わりにクレジットを消費する。
   * remainingAppGenerationCount === 0 かつ remainingCredit >= appGenerationCreditCost の場合に使用。
   */
  use_credit?: boolean;
}

// Spaceオブジェクト
export interface KintoneSpace {
  id?: string;
  name: string;
}

// カスタマイズファイル情報
export interface CustomizeFile {
  url: string;
  file_name: string;
  summary: string;
}

// ジョブ情報（APIレスポンス）
export interface KintoneSystem {
  id: string;
  job_id: string;
  organization_id: string;
  project_id: string;
  subdomain: string;
  space: KintoneSpace;
  status: KintoneSystemStatus;
  logs: KintoneSystemLogs;
  current_process?: string;
  created_at: string; // ISO文字列
  updated_at?: string; // ISO文字列
  created_by: string;
  outputs?: {
    kintone_apps?: {
      app_id: string;
      app_name: string;
      description?: string;
      customize_files?: CustomizeFile[];
    }[];
  };
  error_reason?: string;
}

// プロセスステータス（バックエンドと同じ定義）
export type ProcessStatus = "queued" | "running" | "succeeded" | "failed";

// プロセスログ（バックエンドと同じ定義）
export interface ProcessLog {
  status: ProcessStatus;
}

// ログ（バックエンドと同じ定義）
export interface KintoneSystemLogs {
  GenerateAppSetting?: ProcessLog;
  GenerateFields?: ProcessLog;
  GenerateCustomizeCode?: ProcessLog;
  AdjustFormLayout?: ProcessLog;
}

// プロセス名の型
export type ProcessName = keyof KintoneSystemLogs;

// Kintone認証検証リクエスト
export interface VerifyKintoneAuthRequest {
  subdomain: string;
  login_id: string;
  password: string;
}

// Kintone認証検証結果
export type VerifyKintoneAuthReason =
  | "ok"
  | "authentication_failed"
  | "permission_denied"
  | "subdomain_not_found"
  | "timeout"
  | "rate_limited"
  | "unexpected_error";

export interface VerifyKintoneAuthResult {
  ok: boolean;
  reason: VerifyKintoneAuthReason;
  message?: string;
}

// プロセス名とステップの対応
const PROCESS_STEPS: { processName: ProcessName; step: DevelopmentStep; order: number }[] = [
  { processName: "GenerateAppSetting", step: "app-creation", order: 1 },
  { processName: "GenerateFields", step: "field-creation", order: 2 },
  { processName: "GenerateCustomizeCode", step: "customize-code", order: 3 },
  { processName: "AdjustFormLayout", step: "layout-update", order: 4 },
];

// logsから現在のステップを判定
export const getCurrentStepFromLogs = (logs: KintoneSystemLogs): DevelopmentStep => {
  // runningのプロセスを探す
  for (const { processName, step } of PROCESS_STEPS) {
    if (logs[processName]?.status === "running") {
      return step;
    }
  }
  // runningがなければ最後に成功したプロセスの次のステップ
  let lastSucceededOrder = 0;
  for (const { processName, order } of PROCESS_STEPS) {
    if (logs[processName]?.status === "succeeded" && order > lastSucceededOrder) {
      lastSucceededOrder = order;
    }
  }
  const nextStep = PROCESS_STEPS.find((p) => p.order === lastSucceededOrder + 1);
  return nextStep?.step ?? "app-creation";
};

// logsから全体のステータスを計算
export const getOverallStatusFromLogs = (logs: KintoneSystemLogs): ProcessStatus => {
  const statuses = PROCESS_STEPS.map(({ processName }) => logs[processName]?.status);
  if (statuses.some((s) => s === "failed")) return "failed";
  if (statuses.every((s) => s === "succeeded")) return "succeeded";
  if (statuses.some((s) => s === "running")) return "running";
  return "queued";
};

// プロセス名からステップ番号を取得
export const getStepOrderFromProcessName = (processName: ProcessName): number => {
  return PROCESS_STEPS.find((p) => p.processName === processName)?.order ?? 1;
};

/**
 * バックエンドの汎用エラーメッセージに対して、ユーザー向けアクション案内を付与する。
 * ビジネスエラー（kintone固有メッセージ）は既に案内を含んでいるため、そのまま返す。
 * @sync-target レクシンユーザーメッセージ一覧 > 01_トースト通知一覧 No.155-9〜155-14
 */
const START_ERROR_GUIDANCE: Record<string, string> = {
  不正なエンティティです:
    "時間をおいて再試行してください。継続する場合は管理者へ連絡してください。",
  サーバー内部でエラーが発生しました: "時間をおいて再試行してください。",
  リソースが見つかりません: "ページを再読み込みして再試行してください。",
  ネットワークエラーが発生しました:
    "ネットワーク接続を確認し、しばらく待ってから再試行してください。",
  タイムアウトが発生しました: "しばらく待ってから再試行してください。",
  予期せぬエラーが発生しました:
    "時間をおいて再試行してください。継続する場合は管理者へ連絡してください。",
};

const appendStartErrorGuidance = (message: string): string => {
  const guidance = START_ERROR_GUIDANCE[message];
  return guidance ? `${message}${guidance}` : message;
};

export class KintoneSystemApi {
  private getRequestInfo(method: string, requestBody?: object): RequestInit {
    return {
      method: method,
      headers: commonHeader,
      credentials: "include",
      keepalive: true,
      ...(requestBody && {
        body: JSON.stringify(requestBody),
      }),
    };
  }

  /**
   * プロジェクトのkintoneシステム一覧を取得
   */
  async getKintoneSystemsByProject(
    organizationId: string,
    projectId: string,
  ): Promise<KintoneSystem[] | null> {
    const endpoint = kintoneSystemEndpoints.getKintoneSystemsByProject(organizationId, projectId);
    const requestInfo = this.getRequestInfo("GET");

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return await response.json();
      } else if (response.status === 404) {
        return [];
      } else {
        const errorMessage = await response.json();
        console.error("Failed to get kintone systems:", errorMessage);
        return null;
      }
    } catch (error) {
      console.error("Error fetching kintone systems:", error);
      return null;
    }
  }

  /**
   * kintoneシステムの詳細を取得
   */
  async getKintoneSystem(
    organizationId: string,
    projectId: string,
    kintoneSystemId: string,
  ): Promise<KintoneSystem | null> {
    const endpoint = kintoneSystemEndpoints.getKintoneSystem(
      organizationId,
      projectId,
      kintoneSystemId,
    );
    const requestInfo = this.getRequestInfo("GET");

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return await response.json();
      } else if (response.status === 404) {
        return null;
      } else {
        const errorMessage = await response.json();
        console.error("Failed to get kintone system:", errorMessage.message);
        return null;
      }
    } catch (error) {
      console.error("Error fetching kintone system:", error);
      return null;
    }
  }

  /**
   * kintoneシステムを開始（ジョブ作成）
   */
  async startKintoneSystem(
    organizationId: string,
    projectId: string,
    request: StartKintoneSystemRequest,
  ): Promise<KintoneSystem | null> {
    const endpoint = kintoneSystemEndpoints.startKintoneSystem(organizationId, projectId);
    const requestInfo = this.getRequestInfo("POST", request);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return await response.json();
      } else {
        const errorMessage = await response.json();
        console.error("Failed to start kintone system:", errorMessage.message);
        const message = errorMessage.message
          ? appendStartErrorGuidance(errorMessage.message)
          : "ジョブの開始に失敗しました。時間をおいて再試行してください。";
        throw new Error(message);
      }
    } catch (error) {
      console.error("Error starting kintone system:", error);
      throw error;
    }
  }

  /**
   * kintoneシステムをキャンセル
   */
  async cancelKintoneSystem(
    organizationId: string,
    projectId: string,
    kintoneSystemId: string,
  ): Promise<boolean> {
    const endpoint = kintoneSystemEndpoints.cancelKintoneSystem(
      organizationId,
      projectId,
      kintoneSystemId,
    );
    const requestInfo = this.getRequestInfo("POST");

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return true;
      } else {
        const errorMessage = await response.json();
        console.error("Failed to cancel kintone system:", errorMessage.message);
        return false;
      }
    } catch (error) {
      console.error("Error cancelling kintone system:", error);
      return false;
    }
  }

  /**
   * 進行中のジョブを取得
   */
  async getRunningJob(organizationId: string, projectId: string): Promise<KintoneSystem | null> {
    const systems = await this.getKintoneSystemsByProject(organizationId, projectId);
    if (!systems) return null;

    // queued または running のジョブを探す
    return systems.find((s) => s.status === "queued" || s.status === "running") || null;
  }

  /**
   * Kintone認証を検証
   */
  async verifyKintoneAuth(
    organizationId: string,
    projectId: string,
    request: VerifyKintoneAuthRequest,
  ): Promise<VerifyKintoneAuthResult> {
    const endpoint = kintoneSystemEndpoints.verifyKintoneAuth(organizationId, projectId);
    const requestInfo = this.getRequestInfo("POST", request);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return await response.json();
      } else {
        const errorMessage = await response.json();
        console.error("Failed to verify kintone auth:", errorMessage.message);
        return {
          ok: false,
          reason: "unexpected_error",
          message: errorMessage.message || "認証検証に失敗しました",
        };
      }
    } catch (error) {
      console.error("Error verifying kintone auth:", error);
      return {
        ok: false,
        reason: "unexpected_error",
        message: error instanceof Error ? error.message : "認証検証に失敗しました",
      };
    }
  }
}
